/**
 * Default Response Processor
 *
 * Base processor for agents without specialized handling.
 * Handles standard response normalization and action-based metadata.
 *
 * @module services/responseProcessors/DefaultProcessor
 */

import {
  IResponseProcessor,
  NormalizedResponse,
  ProcessingResult,
  ProcessorContext,
  ChatMessage,
} from './types';
import { ChatMessageResponse } from '@/collections/chat';
import { executeAgentAction } from '@/config/agentActions';

/**
 * Default processor for agents without specialized handling
 *
 * Handles:
 * - Response format detection (old string, old items, new ChatResponse)
 * - Basic message content extraction
 * - Action-based metadata execution
 * - Human handoff handling
 * - Routing directive parsing
 */
export class DefaultProcessor implements IResponseProcessor {
  readonly agentIds = ['*']; // Wildcard - used as default
  readonly description = 'Default processor for standard response handling';

  /**
   * Normalize response - handles old/new format detection
   */
  normalizeResponse(response: ChatMessageResponse): NormalizedResponse {
    let messageContent = '';
    let events: any[] = [];
    let metadata = response.metadata || {};
    let isNewFormat = false;

    const responseAny = response as any;

    // Check for new ChatResponse format in reply
    if (
      response.reply &&
      typeof response.reply === 'object' &&
      Array.isArray((response.reply as any).messages) &&
      Array.isArray((response.reply as any).events)
    ) {
      isNewFormat = true;
      const chatResponse = response.reply as any;
      events = chatResponse.events || [];
      metadata = { ...metadata, ...chatResponse.metadata };

      // Extract message content from messages array
      const messages = chatResponse.messages || [];
      messageContent = messages
        .filter((msg: any) => msg.content && typeof msg.content === 'string')
        .map((msg: any) => msg.content)
        .join('\n\n');
    } else if (typeof response.reply === 'object' && (response.reply as any).items) {
      // Old structured format with items
      const items = (response.reply as any).items || [];
      messageContent = items
        .map((item: any) => (typeof item.content === 'string' ? item.content : ''))
        .join('\n\n');
    } else {
      // Plain string reply
      messageContent = String(response.reply || '');
    }

    // Filter out handoff signals
    messageContent = messageContent.replace(/\[HANDOFF_?SIGNAL\]/g, '').trim();

    return {
      messageContent,
      events,
      metadata,
      isNewFormat,
      regularMessages: [],
      actionMessages: [],
    };
  }

  /**
   * Process normalized response
   */
  async processResponse(
    normalized: NormalizedResponse,
    context: ProcessorContext
  ): Promise<ProcessingResult> {
    const result: ProcessingResult = {
      messagesToAdd: [],
      messagesToRemove: [],
      eventBusEmits: [],
      success: true,
    };

    // Create assistant message
    if (normalized.messageContent.trim()) {
      const assistantMessage: ChatMessage = {
        id: context.generateMessageId(),
        content: normalized.messageContent,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: context.selectedAgent.name,
          avatar: context.selectedAgent.avatar,
        },
        isStreaming: false,
      };
      result.messagesToAdd.push(assistantMessage);
    }

    // Handle action-based metadata
    if (normalized.metadata.action) {
      const actionName = normalized.metadata.action;
      const actionPayload = normalized.metadata.payload || normalized.metadata;

      // Execute the action after a small delay to ensure UI is updated
      setTimeout(() => {
        executeAgentAction(actionName, actionPayload, {
          sessionId: context.conversationId,
          agentId: context.selectedAgent.id,
          userId: context.userId,
        });
      }, 100);
    }

    // Handle human handoff (legacy metadata flag)
    if (normalized.metadata.needs_human_handoff) {
      result.eventBusEmits!.push({
        event: 'open-crisp-for-handoff',
        payload: {
          agentId: context.selectedAgent.id,
          agentName: context.selectedAgent.name,
          conversationId: context.conversationId,
          metadata: normalized.metadata,
          timestamp: new Date().toISOString(),
        },
      });
    }

    // Check for routing directives [ROUTE:agent-id]
    const routingMatch = normalized.messageContent.match(/^\[ROUTE:([^\]]+)\](.*)$/);
    if (routingMatch) {
      result.routeNavigation = {
        targetAgentId: routingMatch[1],
        message: routingMatch[2]?.trim() || `Routing to ${routingMatch[1]} specialist...`,
      };
      // Update the message content to show the routing message
      if (result.messagesToAdd.length > 0 && result.routeNavigation.message) {
        result.messagesToAdd[0].content = result.routeNavigation.message;
      }
    }

    return result;
  }
}
